import React, { useCallback, useEffect, useState } from "react";
import apiClient from "../../utils/apiClient.js";
import { useAdmin } from "../../context/AdminContext.js";
import NotificationModal from "../../components/NotificationModal.js";
import useNotification from "../../hooks/useNotification.js";
import Spinner from "../../components/Spinner.js";
import "./Admin.css";

const SecuritySettingsPage = () => {
  const { admin, refresh } = useAdmin();
  const [status, setStatus] = useState(null);
  const [loading, setLoading] = useState(true);
  const { notification, notify, dismiss } = useNotification();

  // Profile (name/email) state
  const [profileName, setProfileName] = useState(admin?.name || "");
  const [profileEmail, setProfileEmail] = useState(admin?.email || "");
  const [savingProfile, setSavingProfile] = useState(false);

  // Setup wizard state
  const [setupData, setSetupData] = useState(null); // { secret, qrCodeDataUrl }
  const [setupCode, setSetupCode] = useState("");
  const [confirming, setConfirming] = useState(false);
  const [newBackupCodes, setNewBackupCodes] = useState(null);
  const [backupCodesReason, setBackupCodesReason] = useState("setup"); // "setup" | "regenerate"
  const [savedAcknowledged, setSavedAcknowledged] = useState(false);

  // Disable / regenerate state
  const [showDisableForm, setShowDisableForm] = useState(false);
  const [disablePassword, setDisablePassword] = useState("");
  const [disableCode, setDisableCode] = useState("");
  const [disabling, setDisabling] = useState(false);

  const [showRegenForm, setShowRegenForm] = useState(false);
  const [regenPassword, setRegenPassword] = useState("");
  const [regenCode, setRegenCode] = useState("");
  const [regenerating, setRegenerating] = useState(false);

  // Change password state
  const [currentPassword, setCurrentPassword] = useState("");
  const [newPassword, setNewPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [changePasswordCode, setChangePasswordCode] = useState("");
  const [changingPassword, setChangingPassword] = useState(false);

  // SMTP diagnostic state
  const [testingSmtp, setTestingSmtp] = useState(false);
  const [smtpResult, setSmtpResult] = useState(null); // { success, error }

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await apiClient.get("/2fa/status");
      setStatus(data);
    } catch (err) {
      notify("error", "Could not load security settings", "Please refresh the page and try again.");
    } finally {
      setLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const resetSetupState = () => {
    setSetupData(null);
    setSetupCode("");
    setNewBackupCodes(null);
    setSavedAcknowledged(false);
  };

  const handleStartSetup = async () => {
    try {
      const { data } = await apiClient.post("/2fa/setup");
      setSetupData(data);
    } catch (err) {
      notify("error", "Could not start 2FA setup", err.response?.data?.error);
    }
  };

  const handleConfirmSetup = async (e) => {
    e.preventDefault();
    setConfirming(true);
    try {
      const { data } = await apiClient.post("/2fa/confirm", { code: setupCode });
      setBackupCodesReason("setup");
      setNewBackupCodes(data.backupCodes);
      await refresh();
      await load();
    } catch (err) {
      notify("error", "Invalid verification code", err.response?.data?.error);
    } finally {
      setConfirming(false);
    }
  };

  const handleFinishSetup = () => {
    const reason = backupCodesReason;
    resetSetupState();
    notify(
      "success",
      reason === "regenerate" ? "Backup codes regenerated" : "Two-factor authentication enabled",
      reason === "regenerate"
        ? "Older codes no longer work."
        : "Two-factor authentication is now enabled on your account."
    );
  };

  const handleDisable = async (e) => {
    e.preventDefault();
    setDisabling(true);
    try {
      await apiClient.post("/2fa/disable", { password: disablePassword, code: disableCode });
      setShowDisableForm(false);
      setDisablePassword("");
      setDisableCode("");
      await refresh();
      await load();
      notify("success", "Two-factor authentication disabled", "You can re-enable it at any time.");
    } catch (err) {
      notify("error", "Could not disable 2FA", err.response?.data?.error);
    } finally {
      setDisabling(false);
    }
  };

  const handleRegenerate = async (e) => {
    e.preventDefault();
    setRegenerating(true);
    try {
      const { data } = await apiClient.post("/2fa/backup-codes/regenerate", {
        password: regenPassword,
        code: regenCode,
      });
      setBackupCodesReason("regenerate");
      setNewBackupCodes(data.backupCodes);
      setShowRegenForm(false);
      setRegenPassword("");
      setRegenCode("");
      await load();
    } catch (err) {
      notify("error", "Could not regenerate backup codes", err.response?.data?.error);
    } finally {
      setRegenerating(false);
    }
  };

  const handleUpdateProfile = async (e) => {
    e.preventDefault();
    setSavingProfile(true);
    try {
      await apiClient.post("/account/profile", { name: profileName, email: profileEmail });
      await refresh();
      notify("success", "Profile updated", "Your name and email have been saved.");
    } catch (err) {
      notify("error", "Could not update your profile", err.response?.data?.error || "Please try again.");
    } finally {
      setSavingProfile(false);
    }
  };

  const handleChangePassword = async (e) => {
    e.preventDefault();
    if (newPassword.length < 8) {
      notify("warning", "Password too short", "New password must be at least 8 characters.");
      return;
    }
    if (newPassword !== confirmPassword) {
      notify("warning", "Passwords don't match", "Re-enter your new password to confirm it.");
      return;
    }

    setChangingPassword(true);
    try {
      await apiClient.post("/account/change-password", {
        currentPassword,
        newPassword,
        code: status?.enabled ? changePasswordCode : undefined,
      });
      setCurrentPassword("");
      setNewPassword("");
      setConfirmPassword("");
      setChangePasswordCode("");
      notify("success", "Password updated", "Use your new password next time you sign in.");
    } catch (err) {
      notify("error", "Could not update password", err.response?.data?.error || "Please try again.");
    } finally {
      setChangingPassword(false);
    }
  };

  const handleTestSmtp = async () => {
    setTestingSmtp(true);
    setSmtpResult(null);
    try {
      await apiClient.post("/admin/smtp-test");
      setSmtpResult({ success: true });
    } catch (err) {
      setSmtpResult({ success: false, error: err.response?.data?.error || "Could not reach the SMTP server." });
    } finally {
      setTestingSmtp(false);
    }
  };

  if (loading) {
    return <Spinner label="Loading security settings…" />;
  }

  // A fresh set of backup codes (from initial setup or a regeneration) is shown exactly once.
  if (newBackupCodes) {
    return (
      <div className="admin-page">
        <div className="admin-page-header">
          <h1>Save Your Backup Codes</h1>
        </div>
        <div className="admin-2fa-card">
          <p>
            Store these somewhere safe. Each code can be used once to sign in if you lose access to
            your authenticator app. They will not be shown again.
          </p>
          <div className="admin-backup-codes-grid">
            {newBackupCodes.map((code) => (
              <code key={code} className="admin-backup-code">
                {code}
              </code>
            ))}
          </div>
          <label className="admin-checkbox-label">
            <input
              type="checkbox"
              checked={savedAcknowledged}
              onChange={(e) => setSavedAcknowledged(e.target.checked)}
            />
            I have saved these backup codes
          </label>
          <button type="button" disabled={!savedAcknowledged} onClick={handleFinishSetup}>
            Done
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="admin-page">
      <div className="admin-page-header">
        <h1>Security</h1>
        <p>Password and two-factor authentication for your account</p>
      </div>

      <div className="admin-2fa-card">
        <h2 className="admin-2fa-card-title">Profile</h2>
        <form className="admin-2fa-setup" onSubmit={handleUpdateProfile}>
          <div className="admin-form-group">
            <label htmlFor="profile-name">Full name</label>
            <input
              id="profile-name"
              type="text"
              value={profileName}
              onChange={(e) => setProfileName(e.target.value)}
              required
            />
          </div>
          <div className="admin-form-group">
            <label htmlFor="profile-email">Email</label>
            <input
              id="profile-email"
              type="email"
              value={profileEmail}
              onChange={(e) => setProfileEmail(e.target.value)}
              required
            />
          </div>
          <button type="submit" disabled={savingProfile}>
            {savingProfile ? "Saving…" : "Save Profile"}
          </button>
        </form>
      </div>

      <div className="admin-2fa-card">
        <h2 className="admin-2fa-card-title">Change Password</h2>
        <form className="admin-2fa-setup" onSubmit={handleChangePassword}>
          <div className="admin-form-group">
            <label htmlFor="current-password">Current password</label>
            <input
              id="current-password"
              type="password"
              value={currentPassword}
              onChange={(e) => setCurrentPassword(e.target.value)}
              autoComplete="current-password"
              required
            />
          </div>
          <div className="admin-form-group">
            <label htmlFor="new-password">New password</label>
            <input
              id="new-password"
              type="password"
              value={newPassword}
              onChange={(e) => setNewPassword(e.target.value)}
              autoComplete="new-password"
              minLength={8}
              required
            />
          </div>
          <div className="admin-form-group">
            <label htmlFor="confirm-password">Confirm new password</label>
            <input
              id="confirm-password"
              type="password"
              value={confirmPassword}
              onChange={(e) => setConfirmPassword(e.target.value)}
              autoComplete="new-password"
              minLength={8}
              required
            />
          </div>
          {status?.enabled && (
            <div className="admin-form-group">
              <label htmlFor="change-password-code">Verification code</label>
              <input
                id="change-password-code"
                type="text"
                value={changePasswordCode}
                onChange={(e) => setChangePasswordCode(e.target.value)}
                placeholder="123456 or backup code"
                required
              />
            </div>
          )}
          <button type="submit" disabled={changingPassword}>
            {changingPassword ? "Updating…" : "Update Password"}
          </button>
        </form>
      </div>

      <div className="admin-2fa-card">
        <h2 className="admin-2fa-card-title">Email (SMTP)</h2>
        <p className="admin-subtle">
          Booking confirmation emails and invoices are sent through the SMTP credentials in the
          backend's <code>.env</code>. Test the connection here any time without waiting for a real
          booking to fail — this checks the login only, it never sends an email.
        </p>
        <button type="button" onClick={handleTestSmtp} disabled={testingSmtp}>
          {testingSmtp ? (
            <>
              <Spinner size="sm" inline /> Testing…
            </>
          ) : (
            "Test SMTP Connection"
          )}
        </button>
        {smtpResult && (
          <p className={smtpResult.success ? "admin-2fa-badge-on" : "admin-error"}>
            {smtpResult.success
              ? "✓ SMTP login succeeded — credentials are valid."
              : `✗ ${smtpResult.error}`}
          </p>
        )}
        {smtpResult && !smtpResult.success && /535|auth/i.test(smtpResult.error || "") && (
          <p className="admin-subtle">
            A 535 "incorrect authentication data" error almost always means the mailbox needs an
            app-specific password rather than its normal login password (this is required by Gmail
            and most providers once 2-Step Verification is on) — generate one from the mail
            provider's account security settings and update <code>SMTP_PASSWORD</code> in
            <code> backend/.env</code>, then test again.
          </p>
        )}
      </div>

      <div className="admin-2fa-card">
        <h2 className="admin-2fa-card-title">Two-Factor Authentication</h2>
        <div className="admin-2fa-status-row">
          <span className={status?.enabled ? "admin-2fa-badge-on" : "admin-2fa-badge-off"}>
            {status?.enabled ? "Enabled" : "Disabled"}
          </span>
          {status?.enabled && (
            <span className="admin-subtle">{status.backupCodesRemaining} backup codes remaining</span>
          )}
        </div>

        {!status?.enabled && !setupData && (
          <>
            <p>
              Add an extra layer of security to your account using an authenticator app such as
              Google Authenticator, Authy or Microsoft Authenticator.
            </p>
            <button type="button" onClick={handleStartSetup}>
              Enable Two-Factor Authentication
            </button>
          </>
        )}

        {setupData && (
          <form className="admin-2fa-setup" onSubmit={handleConfirmSetup}>
            <p>1. Scan this QR code with your authenticator app:</p>
            <img className="admin-2fa-qr" src={setupData.qrCodeDataUrl} alt="Scan with your authenticator app" />
            <p className="admin-subtle">
              Can't scan it? Enter this code manually: <code>{setupData.secret}</code>
            </p>
            <p>2. Enter the 6-digit code it generates:</p>
            <div className="admin-form-group">
              <input
                type="text"
                inputMode="numeric"
                value={setupCode}
                onChange={(e) => setSetupCode(e.target.value)}
                placeholder="123456"
                autoFocus
                required
              />
            </div>
            <button type="submit" disabled={confirming}>
              {confirming ? "Verifying…" : "Confirm & Enable"}
            </button>
            <button type="button" className="admin-login-back-btn" onClick={resetSetupState}>
              Cancel
            </button>
          </form>
        )}

        {status?.enabled && (
          <div className="admin-2fa-actions">
            {!showRegenForm && (
              <button type="button" className="admin-resend-link" onClick={() => setShowRegenForm(true)}>
                Regenerate Backup Codes
              </button>
            )}
            {!showDisableForm && (
              <button type="button" className="admin-inactive-btn" onClick={() => setShowDisableForm(true)}>
                Disable 2FA
              </button>
            )}
          </div>
        )}

        {showRegenForm && (
          <form className="admin-2fa-confirm-form" onSubmit={handleRegenerate}>
            <p>Confirm your password and a current code to regenerate backup codes:</p>
            <div className="admin-form-group">
              <input
                type="password"
                placeholder="Current password"
                value={regenPassword}
                onChange={(e) => setRegenPassword(e.target.value)}
                required
              />
            </div>
            <div className="admin-form-group">
              <input
                type="text"
                placeholder="Verification code"
                value={regenCode}
                onChange={(e) => setRegenCode(e.target.value)}
                required
              />
            </div>
            <button type="submit" disabled={regenerating}>
              {regenerating ? "Regenerating…" : "Regenerate"}
            </button>
            <button type="button" className="admin-login-back-btn" onClick={() => setShowRegenForm(false)}>
              Cancel
            </button>
          </form>
        )}

        {showDisableForm && (
          <form className="admin-2fa-confirm-form" onSubmit={handleDisable}>
            <p>Confirm your password and a current code to disable 2FA:</p>
            <div className="admin-form-group">
              <input
                type="password"
                placeholder="Current password"
                value={disablePassword}
                onChange={(e) => setDisablePassword(e.target.value)}
                required
              />
            </div>
            <div className="admin-form-group">
              <input
                type="text"
                placeholder="Verification code"
                value={disableCode}
                onChange={(e) => setDisableCode(e.target.value)}
                required
              />
            </div>
            <button type="submit" className="admin-inactive-btn" disabled={disabling}>
              {disabling ? "Disabling…" : "Disable 2FA"}
            </button>
            <button type="button" className="admin-login-back-btn" onClick={() => setShowDisableForm(false)}>
              Cancel
            </button>
          </form>
        )}
      </div>

      {notification && (
        <NotificationModal
          variant={notification.variant}
          title={notification.title}
          message={notification.message}
          onClose={dismiss}
        />
      )}
    </div>
  );
};

export default SecuritySettingsPage;
